import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { BaseElement, customElement, watch } from '../../base-element';
import styles from './radio.scss?inline';

/**
 * @summary Radios allow the user to select a single option from a group.
 *
 * @slot - The radio's label.
 *
 * @event blur - Emitted when the control loses focus.
 * @event focus - Emitted when the control gains focus.
 */

@customElement('ps-radio')
export class RadioWC extends BaseElement {
  static styles = unsafeCSS(styles);

  @state() checked = false;

  @state() protected hasFocus = false;

  /** The radio's value. When selected, the radio group will receive this value. */
  @property() value: string;

  /**
   * The radio's size. When used inside a radio group, the size will be determined by the radio group's size so this
   * attribute can typically be omitted.
   */
  @property({ reflect: true }) size: 'small' | 'medium' | 'large' = 'medium';

  /** Disables the radio. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  constructor() {
    super();
    this.addEventListener('blur', this.handleBlur);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('focus', this.handleFocus);
  }

  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    this.setInitialAttributes();
  }

  private handleBlur = () => {
    this.hasFocus = false;
    this.emit('ps-blur');
  };

  private handleClick = () => {
    if (!this.disabled) {
      this.checked = true;
    }
  };

  private handleFocus = () => {
    this.hasFocus = true;
    this.emit('ps-focus');
  };

  private setInitialAttributes() {
    this.setAttribute('role', 'radio');
    this.setAttribute('tabindex', '-1');
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
  }

  @watch('checked')
  handleCheckedChange() {
    this.setAttribute('aria-checked', this.checked ? 'true' : 'false');
    this.setAttribute('tabindex', this.checked ? '0' : '-1');
  }

  // Not `waitUntilFirstUpdate`: a wrapper such as React's connects the element
  // first and assigns `disabled` afterwards, and that assignment IS the first
  // update, so skipping it left `aria-disabled="false"` on a disabled radio.
  @watch('disabled')
  handleDisabledChange() {
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
  }

  render() {
    return html`
      <span
        class=${classMap({
          'c-radio': true,
          'c-radio--checked': this.checked,
          'c-radio--disabled': this.disabled,
          'c-radio--focused': this.hasFocus,
          [`c-radio--size-${this.size}`]: this.size,
        })}
      >
        <span
          class="${`c-radio__control ${
            this.checked ? 'c-radio__control--checked' : ''
          }`}"
        >
        </span>
        <slot class="c-radio__label"></slot>
      </span>
    `;
  }
}
